import {useMeHasPermission} from "@/src/modules/core/store/me";
import {GetListResponse, type ParamsOptions} from "@/src/modules/core/types";

import type {UpdatePlaceholderRequest} from "../../households/types";
import {meService} from "../../core/services/me";
import {usersApi} from "../api";
import {normalizeUserCreateFormMetadata} from "../lib/normalize-dispatch-center-options";
import {useRolesStore} from "../store";
import {
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserStatus,
  User,
  UserCreateFormMetadata,
} from "../types";

class UsersService {
  async getRoles() {
    const {data} = await usersApi.getRoles();

    const sorted = data.roles.sort((a, b) => a.name.localeCompare(b.name));

    useRolesStore.setState({
      roles: sorted,
    });

    return data;
  }

  async getUsers(options: ParamsOptions): Promise<GetListResponse<User>> {
    const {data} = await usersApi.getUsers(options);
    const users = data.data;
    const meta = data.meta;

    return {data: users, meta};
  }

  async getUser(id: Parameters<typeof usersApi.getUser>[0]) {
    const {data} = await usersApi.getUser(id);

    return data.data;
  }

  async createUser(data: CreateUserRequest) {
    const {
      data: {data: user},
    } = await usersApi.createUser(data);

    return user;
  }

  async updateUser(id: number, data: UpdateUserRequest | UpdateUserStatus) {
    const {
      data: {data: user},
    } = await usersApi.updateUser(id, data);

    return user;
  }

  async deleteUser(id: number) {
    const {
      data: {data: user},
    } = await usersApi.deleteUser(id);

    return user;
  }

  getIsCreateEnabled() {
    return meService.getHasPermission("user store");
  }

  getIsUpdateEnabled() {
    return meService.getHasPermission("user update");
  }

  getIsDeleteEnabled() {
    return meService.getHasPermission("user delete");
  }

  async changePassword(data: {password: string}): Promise<void> {
    await usersApi.changePassword(data);
  }

  async sendAuthToken(id: number): Promise<void> {
    await usersApi.sendAuthToken(id);
  }

  async updatePlaceholder(userId: number, data: UpdatePlaceholderRequest): Promise<boolean> {
    const result = await usersApi.updatePlaceholder(userId, data);
    return result.data.data.welcome_sent;
  }

  async getUserCreateForm(): Promise<UserCreateFormMetadata> {
    const {data} = await usersApi.getUserCreate();
    return normalizeUserCreateFormMetadata(data.data);
  }
}

export const usersService = new UsersService();
